import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import "./userpage.css";
import { GameUserStats, TableInfo } from "../types";
import { Button, FormControlLabel, Switch, Tooltip } from "@mui/material";
import ProjectList from "../ProjectList";
import { LinkOff, Link } from "@mui/icons-material";
import SimpleTableList from "src/components/SimpleTableList";

function UserPage(props: {}) {
  let params = useParams();
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [gameStats, setGameStats] = useState<GameUserStats[]>([]);
  const [bio, setBio] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const isLoading = useRef<boolean>(false);
  const [displayTooltips, setDisplayTooltips] = useState<boolean>(
    localStorage.getItem("display-tooltips-global") === "1" ? true : false,
  );
  const [saveProjectAdminPosition, setSaveProjectAdminPosition] = useState<boolean>(
    localStorage.getItem("project-admin-save-position") === "1" ? true : false,
  );
  const [saveCardViewerPosition, setSaveCardViewerPosition] = useState<boolean>(
    localStorage.getItem("card-viewer-save-position") === "1" ? true : false,
  );

  const isCurrentUser = params.username === localStorage.getItem("user-name");
  const isAdmin = localStorage.getItem("user-role")?.includes("admin");

  useEffect(() => {
    window.addEventListener("scroll", scrollDetection);
    return () => {
      window.removeEventListener("scroll", scrollDetection);
    };
  }, [tables.length, params.username, isLoading.current]);

  useEffect(() => {
    setTables([]);

    fetch("/api/get-user-game-stats/" + "?username=" + params.username, {
      method: "GET",
    })
      .then((response) => {
        console.log(response);
        if (response.ok) {
          return response.json();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        setGameStats(data);
      })
      .catch((error) => {
        console.log(error);
        setStatus(error.statusText);
      });

    fetch("/api/user-bio/" + "?username=" + params.username, {
      method: "GET",
    })
      .then((response) => {
        console.log(response);
        if (response.ok) {
          return response.text();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        setBio(data);
      })
      .catch((error) => {
        console.log(error);
        setStatus(error.statusText);
      });

    fetch("/api/get-user-badges" + "?username=" + params.username, {
      method: "GET",
    })
      .then((response) => {
        console.log(response);
        if (response.ok) {
          return response.text();
        }
        return Promise.reject(response);
      })
      .then((data) => {
        setBadges(JSON.parse(data));
      })
      .catch((error) => {
        console.log(error);
        setStatus(error.statusText);
      });
  }, [params.username]);

  useEffect(() => {
    loadMore();
  }, [params.username]);

  const saveBio = useCallback(
    (bioText: string) => {
      fetch(`/api/user-bio/?username=${params.username}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bioText,
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to save bio");
          }
          setBio(bioText);
          return response.json();
        })
        .catch((error) => {
          console.error("Error saving bio:", error);
        });
    },
    [params.username],
  );

  const handleEnableTooltipsChanged = (evt: React.ChangeEvent<HTMLInputElement>) => {
    evt.target.checked
      ? localStorage.setItem("display-tooltips-global", "1")
      : localStorage.setItem("display-tooltips-global", "0");
    setDisplayTooltips(evt.target.checked);
  };

  function handleToggleSaveProjectAdminPosition(evt: React.ChangeEvent<HTMLInputElement>) {
    evt.target.checked
      ? localStorage.setItem("project-admin-save-position", "1")
      : localStorage.setItem("project-admin-save-position", "0");
    setSaveProjectAdminPosition(evt.target.checked);
  }

  function handleToggleSaveCardViewerPosition(evt: React.ChangeEvent<HTMLInputElement>) {
    evt.target.checked
      ? localStorage.setItem("card-viewer-save-position", "1")
      : localStorage.setItem("card-viewer-save-position", "0");
    setSaveCardViewerPosition(evt.target.checked);
  }

  const loadMore = useCallback(() => {
    fetch("/api/get-table-list/" + "?status=ended&username=" + params.username + "&skip=" + tables.length, {
      method: "GET",
    })
      .then((response) => {
        console.log(response);
        if (response.ok) {
          return response.json();
        }
        isLoading.current = false;
        return Promise.reject(response);
      })
      .then((data) => {
        isLoading.current = false;
        setTables((prevTables) => [...prevTables, ...data]);
      });
  }, [tables.length, params.username]);

  const scrollDetection = useCallback(() => {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
    const clientHeight = document.documentElement.clientHeight || window.innerHeight;
    if (scrollTop + clientHeight >= scrollHeight - 50 && !isLoading.current) {
      isLoading.current = true;
      loadMore();
    }
  }, [tables, params.username, isLoading.current]);

  const myGamePreferences = (
    <div className="user-preferences">
      <h2>General table settings (for all games)</h2>
      <FormControlLabel
        control={<Switch checked={displayTooltips} onChange={handleEnableTooltipsChanged} />}
        label={"Display hover tooltips"}
      />
    </div>
  );

  const projectAdminPreferences = (
    <div className="project-admin-preferences">
      <h2>Project admin settings</h2>
      <FormControlLabel
        control={<Switch checked={saveProjectAdminPosition} onChange={handleToggleSaveProjectAdminPosition} />}
        label={"Save project admin page scroll position"}
      />
      <FormControlLabel
        control={<Switch checked={saveCardViewerPosition} onChange={handleToggleSaveCardViewerPosition} />}
        label={"Save selected card in card viewer"}
      />
    </div>
  );

  let myGamesList = <></>;
  if (gameStats.length) {
    let gameOverviews = gameStats.map((stat) => {
      return (
        <div className="game-stats-wrap">
          <span className="project-name">{stat.project_name}</span>: {stat.amt}
        </div>
      );
    });
    myGamesList = (
      <div className="game-stats-overview-wrap">
        <h2>Games played</h2>
        {gameOverviews}
      </div>
    );
  }
  if (status !== "") {
    return <div>{status}</div>;
  }
  let tableList = <SimpleTableList tables={Object.values(tables)} loadMore={loadMore} />;

  return (
    <div className="user-page">
      <h1>{params.username}</h1>
      <h2>Badges</h2>
      <div>
        {badges.map((b) => (
          <div key={b.name}>
            <h3>{b.displayName}</h3>
            <img src={`/assets/badges/${b.name}.png`} style={{ width: "50px", height: "50px" }}></img>
            <span>{b.description}</span>
          </div>
        ))}
      </div>
      <h2>Bio</h2>
      <div>{bio}</div>
      {isCurrentUser ? (
        <form
          onSubmit={(evt) => {
            evt.preventDefault();
            const form = evt.target as HTMLFormElement; // Cast target as HTMLFormElement
            const bioText = (form.elements.namedItem("bio") as HTMLTextAreaElement).value; // Access the textarea value
            saveBio(bioText);
          }}
        >
          <textarea name="bio" placeholder="Write something about you..."></textarea>
          <br />
          <input type="submit" value="Update bio" />
        </form>
      ) : null}

      {isCurrentUser ? myGamePreferences : null}

      {isAdmin ? projectAdminPreferences : null}

      {myGamesList}
      <h2>Recently finished tables</h2>
      {tableList}
    </div>
  );
}

export type UserBadge = {
  name: string;
  icon: string;
  displayName: string;
  description: string;
};

export type UserInfo = {
  badges?: UserBadge[];
  username: string;
};

function UsernameDisplay(props: { username: string; badges?: UserBadge[]; color?: string; connected?: boolean }) {
  let connectedIndicator = <></>;
  if (props.connected !== undefined) {
    connectedIndicator = (
      <span
        className="connected-indicator"
        style={{
          color: props.connected ? "green" : "red",
        }}
      >
        {props.connected ? <Link /> : <LinkOff />}
      </span>
    );
  }
  let inner = (
    <span className="username" style={{ color: props.color }}>
      {connectedIndicator}
      {props.username}
    </span>
  );
  if (props.username.toLowerCase().startsWith("dummy-")) {
    return inner;
  }
  return (
    <NavLink to={"/user/" + props.username} target="_blank">
      {inner}
      {props.badges?.map((b) => (
        <UserBadge key={b.name} name={b.name} displayName={b.displayName} description={b.description}></UserBadge>
      ))}
    </NavLink>
  );
}

function UserBadge(props: { name: string; description: string; displayName: string }) {
  return (
    <Tooltip
      key={"badge-" + props.name}
      title={
        <div>
          <strong>{props.displayName}</strong>
          <br />
          {props.description}
        </div>
      }
    >
      <img className="user-badge" src={`/assets/badges/${props.name}.png`} alt={props.displayName} />
    </Tooltip>
  );
}

export { UserPage, UsernameDisplay };
